import { DEFAULT_PAGE_INFO, SortInput } from '@apeiron/common'
import { useFrontendConfigContext } from '@apeiron/config'
import {
  PlanetFilterInput,
  PlanetLite,
  PlanetSortBy,
  PlanetStage
} from '@apeiron/planet'
import {
  deserializeListData,
  deserializePageInfo,
  PageInfo,
  PageInput,
  serializePagingInput
} from '@apeiron/temp'
import { useLazyQuery } from '@apollo/client'
import { planetLiteSchema } from '@src/deserialize/yup/planet'
import graphql from '@src/graphql/query/getPlanets'
import { PlanetLiteDeserializeOption } from '@src/types/deserialize'
import { deserializePlanetLite } from '@src/util/apeiron/planet'
import * as R from 'ramda'
import { useCallback, useState } from 'react'

const useGetPlanets = (): Hook => {
  const [getPlanets, { loading, called }] = useLazyQuery(graphql)

  const { config } = useFrontendConfigContext()

  const [data, setData] = useState<PlanetLite[]>([])

  const [pageInfo, setPageInfo] = useState<PageInfo>(DEFAULT_PAGE_INFO)

  const fetch = useCallback(
    async (
      request: Request,
      option: PlanetLiteDeserializeOption = {}
    ): Promise<Response> => {
      const planetIds = R.propOr([], 'planetIds', request) as string[]

      const stage = R.propOr(null, 'stage', request)

      const pageInput = R.propOr({}, 'pageInput', request) as PageInput

      const filterInput = R.propOr(
        {},
        'filterInput',
        request
      ) as PlanetFilterInput

      const sortInput = R.propOr(
        {
          sortBy: PlanetSortBy.PriceAsc
        },
        'sortInput',
        request
      ) as SortInput<PlanetSortBy>

      const variables = {
        filterInput: {
          ...filterInput,
          ...(!R.isEmpty(planetIds) ? { planetID: planetIds } : {}),
          ...(!R.isNil(stage) ? { stage } : {})
        },
        pagingInput: serializePagingInput(pageInput),
        sortInput
      }

      const response = await getPlanets({ variables })

      const planets = deserializeListData<PlanetLite>(
        response,
        planetLiteSchema,
        config
      )

      const deserializedPageInfo = deserializePageInfo(response, pageInput)

      const patchedPlanets = R.map(
        planet => deserializePlanetLite(planet, option),
        planets
      )

      setData(patchedPlanets)
      setPageInfo(deserializedPageInfo)

      return {
        data: patchedPlanets,
        pageInfo: deserializedPageInfo
      }
    },
    [config, getPlanets]
  )

  const clean = useCallback(() => {
    setData([])
    setPageInfo(DEFAULT_PAGE_INFO)
  }, [])

  return {
    fetch,
    clean,
    data,
    pageInfo,
    loading,
    called
  }
}

export type Request = {
  planetIds?: string[]
  stage?: PlanetStage
  pageInput?: PageInput
  filterInput?: PlanetFilterInput
  sortInput?: SortInput<PlanetSortBy>
}

type Response = {
  data: PlanetLite[]
  pageInfo: PageInfo
}

type Hook = {
  fetch: (request: Request) => Promise<Response>
  clean: () => void
  data: PlanetLite[]
  pageInfo: PageInfo
  loading: boolean
  called: boolean
}

export default useGetPlanets
